import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Trash2, Ban } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TZ = "America/New_York";
const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayNY = () => new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const fmtTime = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; return `${((h + 11) % 12) + 1}:${pad(m)} ${ap}`; };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export const SessionCalendar = ({ sessions = [], onChange, defaultTime = "18:00", defaultCapacity = 10 }) => {
  const today = todayNY();
  const [ty, tm] = today.split("-").map(Number);
  const [view, setView] = useState({ y: ty, m: tm - 1 });
  const [selected, setSelected] = useState([]);
  const [activeDate, setActiveDate] = useState(null);
  const [time, setTime] = useState(defaultTime || "18:00");
  const [cap, setCap] = useState(defaultCapacity || 10);
  const [blocks, setBlocks] = useState([]);
  const [blockForm, setBlockForm] = useState(null);
  const loadBlocks = () => api.get("/admin/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  useEffect(() => { loadBlocks(); }, []);
  const blocksByDate = useMemo(() => blocks.reduce((acc, b) => { (acc[b.date] ||= []).push(b); return acc; }, {}), [blocks]);
  const saveBlock = async () => {
    const { id, ...data } = blockForm;
    if (!data.all_day && (!data.start_time || !data.end_time)) return toast.error("Enter start and end time");
    if (!data.all_day && data.end_time <= data.start_time) return toast.error("End time must be after start time");
    try {
      if (id) await api.put(`/admin/blocks/${id}`, data); else await api.post("/admin/blocks", data);
      toast.success(id ? "Block updated" : "Time blocked"); setBlockForm(null); loadBlocks();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save block"); }
  };
  const deleteBlock = async (b) => {
    if (!window.confirm(`Delete block "${b.title || "Blocked"}" on ${b.date}?`)) return;
    await api.delete(`/admin/blocks/${b.id}`); toast.success("Block removed"); setBlockForm(null); loadBlocks();
  };

  const byDate = useMemo(() => sessions.reduce((acc, s) => { if (s.date) (acc[s.date] ||= []).push(s); return acc; }, {}), [sessions]);

  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1).getDay();
    const days = new Date(view.y, view.m + 1, 0).getDate();
    const arr = Array(first).fill(null);
    for (let d = 1; d <= days; d++) arr.push(iso(view.y, view.m, d));
    while (arr.length % 7) arr.push(null);
    return arr;
  }, [view]);

  const nav = (dir) => setView((v) => { const m = v.m + dir; return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 }; });
  const clickDate = (d) => {
    if (byDate[d] || blocksByDate[d]) setActiveDate(activeDate === d ? null : d); else setActiveDate(d);
    setSelected((s) => (s.includes(d) ? s.filter((x) => x !== d) : [...s, d]));
  };

  const addSelected = () => {
    if (!selected.length) return toast.error("Select at least one date on the calendar");
    if (!time) return toast.error("Enter a start time");
    const exists = new Set(sessions.map((s) => `${s.date}|${s.start_time}`));
    const fresh = selected.filter((d) => !exists.has(`${d}|${time}`)).sort();
    const skipped = selected.length - fresh.length;
    const created = fresh.map((d, i) => ({ id: `sess-${Date.now()}-${i}`, date: d, start_time: time, capacity: Number(cap) || 0, booked: 0, sold_out: false, unavailable: false, hidden: false }));
    onChange([...sessions, ...created]);
    setSelected([]);
    if (created.length) toast.success(`Added ${created.length} session${created.length > 1 ? "s" : ""}${skipped ? ` (${skipped} duplicate${skipped > 1 ? "s" : ""} skipped)` : ""}`);
    else toast.error("All selected dates already have a session at that time");
  };

  const patch = (id, p) => onChange(sessions.map((s) => (s.id === id ? { ...s, ...p } : s)));
  const remove = (s) => {
    if ((s.booked || 0) > 0) return toast.error("This session has bookings and cannot be deleted. Mark it unavailable or hidden instead.");
    if (!window.confirm(`Remove session on ${s.date} at ${fmtTime(s.start_time)}?`)) return;
    const next = sessions.filter((x) => x.id !== s.id);
    onChange(next);
    if (!next.some((x) => x.date === s.date)) setActiveDate(null);
  };

  const active = activeDate ? (byDate[activeDate] || []) : [];

  return (
    <div className="mt-4 grid lg:grid-cols-[minmax(0,1fr)_280px] gap-4" data-testid="session-calendar">
      <div className="bg-white rounded-2xl p-3 sm:p-4">
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={() => nav(-1)} data-testid="cal-prev-month" className="p-2 rounded-full hover:bg-cream-alt" aria-label="Previous month"><ChevronLeft size={16}/></button>
          <div className="font-serif text-base font-semibold" data-testid="cal-month-label">{MONTHS[view.m]} {view.y}</div>
          <button type="button" onClick={() => nav(1)} data-testid="cal-next-month" className="p-2 rounded-full hover:bg-cream-alt" aria-label="Next month"><ChevronRight size={16}/></button>
        </div>
        <div className="grid grid-cols-7 text-center text-[11px] font-medium text-charcoal-muted mb-1">{DOW.map((d) => <div key={d}>{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={`e-${i}`}/>;
            const has = byDate[d];
            const blk = blocksByDate[d];
            const sel = selected.includes(d);
            const isActive = activeDate === d;
            const allOff = has && has.every((s) => s.hidden || s.unavailable || s.sold_out);
            return (
              <button type="button" key={d} onClick={() => clickDate(d)} data-testid={`cal-day-${d}`} aria-pressed={sel} title={blk ? blk.map((b) => b.title || "Blocked").join(", ") : undefined}
                className={`relative aspect-square max-h-14 rounded-xl text-sm flex flex-col items-center justify-center transition-colors
                  ${sel ? "bg-terracotta text-white font-semibold" : blk ? "bg-red-50 hover:bg-red-100" : has ? "bg-cream-alt/70 hover:bg-cream-alt" : "hover:bg-cream-alt/60"}
                  ${isActive && !sel ? "ring-2 ring-terracotta" : ""} ${d === today && !sel ? "font-bold underline" : ""}`}>
                {Number(d.slice(-2))}
                {blk && <Ban size={9} className={`absolute top-1 right-1 ${sel ? "text-white" : "text-red-500"}`} data-testid={`cal-block-indicator-${d}`}/>}
                {has && <span className={`absolute bottom-1 flex gap-0.5`}>{has.slice(0, 3).map((s) => <span key={s.id} className={`w-1.5 h-1.5 rounded-full ${sel ? "bg-white" : allOff ? "bg-charcoal-muted/50" : "bg-terracotta"}`}/>)}</span>}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-charcoal-muted">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-terracotta inline-block"/> Selected</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-terracotta inline-block"/> Has session</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-charcoal-muted/50 inline-block"/> Hidden / unavailable / sold out</span>
          <span className="inline-flex items-center gap-1"><Ban size={10} className="text-red-500"/> Blocked time</span>
        </div>
      </div>

      <div className="space-y-3">
        <div className="bg-white rounded-2xl p-4" data-testid="bulk-add-panel">
          <div className="text-sm font-semibold" data-testid="selected-count">{selected.length} date{selected.length === 1 ? "" : "s"} selected</div>
          <div className="text-[11px] text-charcoal-muted mb-3">Times are America/New_York.</div>
          <label className="text-xs block">Start time
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="bulk-start-time" className="mt-1 w-full rounded-xl border border-cream-alt px-3 py-2"/>
          </label>
          <label className="text-xs block mt-2">Capacity
            <input type="number" min="0" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="bulk-capacity" className="mt-1 w-full rounded-xl border border-cream-alt px-3 py-2"/>
          </label>
          <div className="flex gap-2 mt-3">
            <button type="button" onClick={addSelected} disabled={!selected.length} data-testid="add-selected-dates-btn" className="flex-1 rounded-full bg-terracotta text-white text-xs font-semibold px-4 py-2 disabled:opacity-40">Add Selected Dates</button>
            {selected.length > 0 && <button type="button" onClick={() => setSelected([])} data-testid="clear-selected-btn" className="rounded-full bg-cream-alt text-xs px-3 py-2">Clear</button>}
          </div>
        </div>

        {activeDate && active.length > 0 && (
          <div className="bg-white rounded-2xl p-4" data-testid="edit-date-panel">
            <div className="text-sm font-semibold mb-2">Sessions on {activeDate}</div>
            <div className="space-y-3">
              {active.map((s) => (
                <div key={s.id} className="rounded-xl border border-cream-alt p-3 text-xs space-y-2" data-testid={`edit-session-${s.id}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <label>Start
                      <input type="time" value={s.start_time || ""} onChange={(e) => patch(s.id, { start_time: e.target.value })} data-testid={`edit-session-time-${s.id}`} className="mt-1 w-full rounded-lg border border-cream-alt px-2 py-1.5"/>
                    </label>
                    <label>Capacity
                      <input type="number" min={s.booked || 0} value={s.capacity ?? 0} onChange={(e) => patch(s.id, { capacity: Number(e.target.value) })} data-testid={`edit-session-cap-${s.id}`} className="mt-1 w-full rounded-lg border border-cream-alt px-2 py-1.5"/>
                    </label>
                  </div>
                  <div className="text-charcoal-muted">Booked: <b>{s.booked || 0}</b></div>
                  <div className="flex flex-wrap gap-2">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!s.sold_out} onChange={(e) => patch(s.id, { sold_out: e.target.checked })}/> Sold out</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!s.unavailable} onChange={(e) => patch(s.id, { unavailable: e.target.checked })}/> Unavailable</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!s.hidden} onChange={(e) => patch(s.id, { hidden: e.target.checked })}/> Hide</label>
                  </div>
                  <button type="button" onClick={() => remove(s)} data-testid={`edit-session-remove-${s.id}`} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs ${(s.booked || 0) > 0 ? "bg-cream-alt text-charcoal-muted cursor-not-allowed" : "bg-red-50 text-red-600"}`}><Trash2 size={12}/> {(s.booked || 0) > 0 ? "Has bookings" : "Remove"}</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeDate && (
          <div className="bg-white rounded-2xl p-4" data-testid="blocks-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Blocked time on {activeDate}</div>
              <button type="button" onClick={() => setBlockForm({ date: activeDate, all_day: false, start_time: "16:00", end_time: "19:00", title: "" })} data-testid="block-time-btn" className="rounded-full bg-charcoal text-white text-[11px] font-semibold px-3 py-1.5 inline-flex items-center gap-1"><Ban size={11}/> Block Time</button>
            </div>
            {(blocksByDate[activeDate] || []).length === 0 && !blockForm && <div className="text-[11px] text-charcoal-muted">No blocks on this date.</div>}
            <div className="space-y-1.5">
              {(blocksByDate[activeDate] || []).map((b) => (
                <button type="button" key={b.id} onClick={() => setBlockForm({ ...b })} data-testid={`block-item-${b.id}`} className="w-full text-left rounded-xl bg-red-50 px-3 py-2 text-xs hover:bg-red-100">
                  <div className="font-semibold text-red-700">{b.title || "Blocked"}</div>
                  <div className="text-charcoal-muted">{b.all_day ? "All day" : `${fmtTime(b.start_time)} – ${fmtTime(b.end_time)}`}</div>
                </button>
              ))}
            </div>
            {blockForm && (
              <div className="mt-3 rounded-xl border border-cream-alt p-3 text-xs space-y-2" data-testid="block-form">
                <div className="font-semibold">{blockForm.id ? "Edit block" : "New block"}</div>
                <label className="block">Date
                  <input type="date" value={blockForm.date} onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })} data-testid="block-date" className="mt-1 w-full rounded-lg border border-cream-alt px-2 py-1.5"/>
                </label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!blockForm.all_day} onChange={(e) => setBlockForm({ ...blockForm, all_day: e.target.checked })} data-testid="block-all-day"/> All Day</label>
                {!blockForm.all_day && (
                  <div className="grid grid-cols-2 gap-2">
                    <label>Start Time
                      <input type="time" value={blockForm.start_time || ""} onChange={(e) => setBlockForm({ ...blockForm, start_time: e.target.value })} data-testid="block-start-time" className="mt-1 w-full rounded-lg border border-cream-alt px-2 py-1.5"/>
                    </label>
                    <label>End Time
                      <input type="time" value={blockForm.end_time || ""} onChange={(e) => setBlockForm({ ...blockForm, end_time: e.target.value })} data-testid="block-end-time" className="mt-1 w-full rounded-lg border border-cream-alt px-2 py-1.5"/>
                    </label>
                  </div>
                )}
                <label className="block">Title / Note
                  <input type="text" value={blockForm.title || ""} onChange={(e) => setBlockForm({ ...blockForm, title: e.target.value })} placeholder="Studio Rental - Tiana" data-testid="block-title" className="mt-1 w-full rounded-lg border border-cream-alt px-2 py-1.5"/>
                </label>
                <div className="text-[10px] text-charcoal-muted">Admin-only. Customers cannot book overlapping times and never see this note.</div>
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={saveBlock} data-testid="block-save-btn" className="flex-1 rounded-full bg-terracotta text-white font-semibold px-3 py-1.5">{blockForm.id ? "Update" : "Save Block"}</button>
                  <button type="button" onClick={() => setBlockForm(null)} data-testid="block-cancel-btn" className="rounded-full bg-cream-alt px-3 py-1.5">Cancel</button>
                  {blockForm.id && <button type="button" onClick={() => deleteBlock(blockForm)} data-testid="block-delete-btn" className="rounded-full bg-red-50 text-red-600 px-3 py-1.5"><Trash2 size={12}/></button>}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="text-[11px] text-charcoal-muted px-1" data-testid="sessions-total">{sessions.length} session{sessions.length === 1 ? "" : "s"} total. Click a date with a dot to edit it. Remember to save the workshop.</div>
      </div>
    </div>
  );
};
